import type { WorkshopBookingListQuery, WorkshopBookingState, WorkshopBookingView } from '@chukua/common';
import { Button, Tag, type TableColumnsType } from 'antd';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import { BOOKING_STATE_LABELS, formatWindow } from '../policies/booking-labels';
import type { BookingRef } from '../hooks/use-schedule-dialogs';

const STATE_COLORS: Record<WorkshopBookingState, string> = { live: 'processing', cancelled: 'default', superseded: 'default' };

interface BuildBookingColumnsArgs {
  list: Pick<ListQueryControls<WorkshopBookingListQuery>, 'sortOrderFor' | 'filteredValueFor'>;
  /** Null hides the actions column: `maintenance.view` or `dispatch.view` alone reads the list. */
  actions: { onMove: (booking: BookingRef) => void; onCancel: (booking: BookingRef) => void } | null;
}

const toRef = (row: WorkshopBookingView): BookingRef => ({ id: row.id, workshopName: row.workshopName, startsAt: row.startsAt, endsAt: row.endsAt });

/** Each column's `key` is its sort field or filter param in `workshopBookingListQuerySchema`; truck and workshop are searched or filtered above the table. */
export function buildBookingColumns({ list, actions }: BuildBookingColumnsArgs): TableColumnsType<WorkshopBookingView> {
  const columns: TableColumnsType<WorkshopBookingView> = [
    { title: 'Truck', dataIndex: 'truckRegistration', key: 'truckRegistration' },
    { title: 'Workshop', dataIndex: 'workshopName', key: 'workshopName' },
    {
      title: 'When',
      key: 'startsAt',
      sorter: true,
      sortOrder: list.sortOrderFor('startsAt'),
      render: (_, row) => formatWindow(row.startsAt, row.endsAt),
    },
    {
      title: 'Status',
      key: 'state',
      filters: Object.entries(BOOKING_STATE_LABELS).map(([value, text]) => ({ text, value })),
      filteredValue: list.filteredValueFor('state'),
      render: (_, row) => (
        <div className="flex flex-col items-start gap-1">
          <Tag color={STATE_COLORS[row.state]}>{BOOKING_STATE_LABELS[row.state]}</Tag>
          {row.endReason && <span className="text-sm text-muted">{row.endReason}</span>}
        </div>
      ),
    },
  ];
  if (actions) {
    columns.push({
      title: '',
      key: 'actions',
      render: (_, row) =>
        row.state === 'live' && (
          <div className="flex gap-2">
            <Button size="small" onClick={() => actions.onMove(toRef(row))}>
              Move
            </Button>
            <Button size="small" danger onClick={() => actions.onCancel(toRef(row))}>
              Cancel
            </Button>
          </div>
        ),
    });
  }
  return columns;
}
